/**
 * MathInline — inline-mode KaTeX renderer for theorem statements,
 * universal quantifiers, and mode-pill semantics that need to render
 * mid-paragraph without a baseline jump.
 *
 * Inline math intentionally avoids the framing chrome that
 * {@link MathBlock} carries — there is no border, no padding, and no
 * caption surface. Theme tokens still flow from `tokens.data.json` via
 * the design system so the rendered glyphs inherit the surrounding
 * color cascade.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import katex, { type ParseError } from "katex";
import React, { useMemo } from "react";
import { TrustedHtml } from "../TrustedHtml.js";
import { createTrustedHtml, type TrustedHtmlValue } from "../../security/trustedHtml.js";

export interface MathInlineProps {
  /** LaTeX source rendered in inline mode. */
  readonly children: string;
  /**
   * Accessible label announced by assistive tech. Falls back to the
   * literal LaTeX source so the meaning survives even when MathML is
   * suppressed.
   */
  readonly ariaLabel?: string;
}

interface RenderResult {
  readonly html?: TrustedHtmlValue;
  readonly error?: string;
}

function renderInline(source: string): RenderResult {
  try {
    const html = katex.renderToString(source, {
      displayMode: false,
      throwOnError: false,
      strict: "ignore",
      output: "html",
      trust: false,
    });
    return { html: createTrustedHtml(html, "katex") };
  } catch (error) {
    const message = (error as ParseError | Error).message;
    return { error: message };
  }
}

/** Inline-mode KaTeX span. */
export const MathInline = React.memo(function MathInline({
  children,
  ariaLabel,
}: MathInlineProps): React.JSX.Element {
  const { html } = useMemo(() => renderInline(children), [children]);
  if (html === undefined) {
    return (
      <span role="math" aria-label={ariaLabel ?? children} data-csk-math="inline">
        {children}
      </span>
    );
  }
  return (
    <TrustedHtml
      role="math"
      aria-label={ariaLabel ?? children}
      data-csk-math="inline"
      element="span"
      html={html}
    />
  );
});
